import {
  PodcastSocialStatsResponse,
  PodcastSocialStatsArgs,
  getPodcastSocialShareStats,
} from 'api/services';
import { useCallback } from 'react';
import { UseQueryOptions, useQuery } from 'react-query';
import { embedVideoQueries } from './queryKeys';
import {
  episodeTitleSelector,
  podcastSocialShareStatsDetailsSelector,
  podcastSocialStatsSummarySelector,
} from './selectors';

type QueryKey = ReturnType<typeof embedVideoQueries.podcast>;

function useGetPodcastSocialShareStats<TData = PodcastSocialStatsResponse>(
  args: Partial<PodcastSocialStatsArgs>,
  opts?: Omit<
    UseQueryOptions<PodcastSocialStatsResponse, unknown, TData, QueryKey>,
    'queryKey' | 'queryFn'
  >,
) {
  return useQuery<PodcastSocialStatsResponse, unknown, TData, QueryKey>(
    embedVideoQueries.podcast(args.podcastId, args.platform),
    ({ queryKey: [{ podcastId, platform }] }) =>
      getPodcastSocialShareStats({ podcastId: podcastId as string, platform }),
    {
      ...opts,
      enabled: !!args.podcastId,
    },
  );
}

export function useGetPodcastSocialShareStatsDetails(podcastId?: string) {
  return useGetPodcastSocialShareStats(
    { podcastId },
    {
      select: podcastSocialShareStatsDetailsSelector,
    },
  );
}

export function useGetPodcastSocialStatsSummary(podcastId?: string) {
  return useGetPodcastSocialShareStats(
    {
      podcastId,
      platform: [
        'facebook',
        'instagram',
        'linkedin',
        'threads',
        'twitter',
        'tiktok',
      ],
    },
    { select: podcastSocialStatsSummarySelector },
  );
}

export function useGetPodcastYoutubeStatsSummary(podcastId?: string) {
  return useGetPodcastSocialShareStats(
    {
      podcastId,
      platform: ['youtube'],
    },
    { select: podcastSocialStatsSummarySelector },
  );
}

export function useGetEpisodeTitleFromSocialStats(
  podcastId: string | undefined,
  episodeId: string | undefined,
) {
  const selector = useCallback(
    (d: PodcastSocialStatsResponse) => episodeTitleSelector(d, episodeId),
    [episodeId],
  );
  return useGetPodcastSocialShareStats(
    { podcastId },
    {
      select: selector,
    },
  );
}
